import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, Eye, EyeOff, AlertCircle, CheckCircle, Shield } from 'lucide-react';
import apiClient from '../../utils/apiClient';

interface PasswordUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PasswordValidation {
  hasMinLength: boolean;
  hasLetterAndNumber: boolean;
  passwordsMatch: boolean;
}

const PasswordUpdateModal: React.FC<PasswordUpdateModalProps> = ({ isOpen, onClose }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [rateLimitRetryAfter, setRateLimitRetryAfter] = useState<number | null>(null);

  // Password validation state
  const [validation, setValidation] = useState<PasswordValidation>({
    hasMinLength: false,
    hasLetterAndNumber: false,
    passwordsMatch: false,
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Clear all fields after a short delay (for animation)
      setTimeout(() => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setError('');
        setSuccess(false);
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        setRateLimitRetryAfter(null);
      }, 300);
    }
  }, [isOpen]);

  // Validate password in real-time
  useEffect(() => {
    const hasMinLength = newPassword.length >= 8;
    const hasLetterAndNumber = /[a-zA-Z]/.test(newPassword) && /[0-9]/.test(newPassword);
    const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

    setValidation({
      hasMinLength,
      hasLetterAndNumber,
      passwordsMatch,
    });
  }, [newPassword, confirmPassword]);

  // Countdown timer for rate limit
  useEffect(() => {
    if (rateLimitRetryAfter === null) return;

    if (rateLimitRetryAfter <= 0) {
      setRateLimitRetryAfter(null);
      setError('');
      return;
    }

    const timer = setTimeout(() => {
      setRateLimitRetryAfter(rateLimitRetryAfter - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [rateLimitRetryAfter]);

  const isFormValid = () => {
    return (
      currentPassword.length > 0 &&
      validation.hasMinLength &&
      validation.hasLetterAndNumber &&
      validation.passwordsMatch
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setError('');

    // Validate form
    if (!isFormValid()) {
      setError('Please ensure all password requirements are met');
      return;
    }

    setLoading(true);

    try {
      const response = await apiClient.patch('/auth/update-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });

      // Success!
      setSuccess(true);

      // Clear sensitive data immediately
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Show success message for 3 seconds then close
      setTimeout(() => {
        onClose();
      }, 3000);

    } catch (err: any) {
      // Handle specific error cases
      if (err.response?.status === 400) {
        // Current password incorrect or validation failed
        const message = err.response?.data?.detail || err.response?.data?.message;
        setError(message || 'The current password you entered is incorrect. Please try again.');
      } else if (err.response?.status === 422) {
        // Validation errors
        const detail = err.response?.data?.detail;
        if (Array.isArray(detail)) {
          // Handle FastAPI validation errors
          const validationMessages = detail.map((err: any) => err.msg).join(', ');
          setError(`Validation error: ${validationMessages}`);
        } else {
          setError(detail || 'Password requirements not met. Please check your new password.');
        }
      } else if (err.response?.status === 429) {
        // Rate limit exceeded
        const retryAfter = err.response?.data?.retry_after || 3600; // Default 1 hour
        setRateLimitRetryAfter(retryAfter);
        setError(`Too many password change attempts. Please try again in ${Math.ceil(retryAfter / 60)} minutes.`);
      } else if (err.response?.status === 401) {
        // Unauthorized - token expired or invalid
        setError('Your session has expired. Please log in again.');
        // The apiClient interceptor should handle redirect to login
      } else {
        // Generic error
        setError(err.message || 'Failed to update password. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatRetryTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds} seconds`;
    const minutes = Math.ceil(seconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-gradient-to-br from-gray-800/95 to-gray-900/95 backdrop-blur-xl rounded-2xl border border-gray-700/50 shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-700/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/10 rounded-xl border border-amber-500/20">
                <Lock className="w-5 h-5 text-amber-400" />
              </div>
              <h2 className="text-xl font-semibold text-white">Change Password</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700/50 rounded-xl transition-colors"
              disabled={loading}
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Success Message */}
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mx-6 mt-6 p-4 bg-green-500/10 border border-green-500/20 rounded-xl flex items-start gap-3"
            >
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-green-400 font-medium">Password Updated Successfully!</p>
                <p className="text-green-400/70 text-sm mt-1">
                  A confirmation email has been sent to your registered email address.
                </p>
              </div>
            </motion.div>
          )}

          {/* Error Message */}
          {error && !success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mx-6 mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3"
            >
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-400 text-sm">{error}</p>
                {rateLimitRetryAfter !== null && rateLimitRetryAfter > 0 && (
                  <p className="text-red-400/70 text-xs mt-2">
                    Retry in: {formatRetryTime(rateLimitRetryAfter)}
                  </p>
                )}
              </div>
            </motion.div>
          )}

          {/* Form */}
          {!success && (
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Security Notice */}
              <div className="p-4 bg-blue-500/5 border border-blue-500/10 rounded-xl flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-blue-400 text-sm font-medium">Security Tip</p>
                  <p className="text-blue-400/70 text-xs mt-1">
                    Use a strong, unique password that you don't use on other websites.
                  </p>
                </div>
              </div>

              {/* Current Password */}
              <div className="space-y-2">
                <label className="text-white font-medium text-sm">Current Password *</label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-amber-500/50 focus:ring-2 focus:ring-amber-500/20 transition-all pr-12"
                    placeholder="Enter your current password"
                    disabled={loading || rateLimitRetryAfter !== null}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-700/50 rounded-lg transition-colors"
                    disabled={loading}
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="w-4 h-4 text-gray-400" />
                    ) : (
                      <Eye className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div className="space-y-2">
                <label className="text-white font-medium text-sm">New Password *</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-amber-500/50 focus:ring-2 focus:ring-amber-500/20 transition-all pr-12"
                    placeholder="Enter your new password"
                    disabled={loading || rateLimitRetryAfter !== null}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-700/50 rounded-lg transition-colors"
                    disabled={loading}
                  >
                    {showNewPassword ? (
                      <EyeOff className="w-4 h-4 text-gray-400" />
                    ) : (
                      <Eye className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <label className="text-white font-medium text-sm">Confirm New Password *</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-amber-500/50 focus:ring-2 focus:ring-amber-500/20 transition-all pr-12"
                    placeholder="Confirm your new password"
                    disabled={loading || rateLimitRetryAfter !== null}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-700/50 rounded-lg transition-colors"
                    disabled={loading}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-4 h-4 text-gray-400" />
                    ) : (
                      <Eye className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              {/* Password Requirements */}
              {newPassword.length > 0 && (
                <div className="space-y-2 p-4 bg-gray-900/30 border border-gray-700/30 rounded-xl">
                  <p className="text-white text-sm font-medium mb-3">Password Requirements:</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      {validation.hasMinLength ? (
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-gray-600" />
                      )}
                      <span className={`text-sm ${validation.hasMinLength ? 'text-green-400' : 'text-gray-400'}`}>
                        At least 8 characters
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {validation.hasLetterAndNumber ? (
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-gray-600" />
                      )}
                      <span className={`text-sm ${validation.hasLetterAndNumber ? 'text-green-400' : 'text-gray-400'}`}>
                        Contains letters and numbers
                      </span>
                    </div>
                    {confirmPassword.length > 0 && (
                      <div className="flex items-center gap-2">
                        {validation.passwordsMatch ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : (
                          <div className="w-4 h-4 rounded-full border-2 border-gray-600" />
                        )}
                        <span className={`text-sm ${validation.passwordsMatch ? 'text-green-400' : 'text-gray-400'}`}>
                          Passwords match
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-gray-700/50 text-white rounded-xl font-medium hover:bg-gray-700/70 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !isFormValid() || rateLimitRetryAfter !== null}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-medium hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-amber-500/25"
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PasswordUpdateModal;